import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useGT } from "gt-react";
import {
  useServersStore,
  useQueriesStore,
  useAppStore,
  addQuery,
  deleteQuery,
  deleteQueries,
  selectQueryInFocusedPane,
  updateQueryInDb,
  toggleQueryPin,
  toggleQueriesPin,
  reorderPinnedQueries,
  moveSavedQueryToCategory,
  moveSavedQueryToPinned,
  isAdHocQuery,
  useExecutionTrackingStore,
} from "../store";
import { toast } from "./Toasts";
import type { StoredQuery } from "../persistence";
import {
  Plus,
  Trash2,
  X,
  Check,
  PanelRight,
  Pencil,
  ClipboardPaste,
  Copy,
  MessageSquareText,
  MoreVertical,
  BookOpen,
  Pin,
  PinOff,
  GripVertical,
  Globe,
  Loader2,
} from "lucide-react";

const isMac = navigator.platform.includes("Mac");

function GqlIcon({ size = 11, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M12 2L3.5 7v10L12 22l8.5-5V7L12 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="12" cy="2" r="1.5" fill="currentColor" />
      <circle cx="3.5" cy="7" r="1.5" fill="currentColor" />
      <circle cx="20.5" cy="7" r="1.5" fill="currentColor" />
      <circle cx="3.5" cy="17" r="1.5" fill="currentColor" />
      <circle cx="20.5" cy="17" r="1.5" fill="currentColor" />
      <circle cx="12" cy="22" r="1.5" fill="currentColor" />
    </svg>
  );
}

// ── XML Serialize / Parse ────────────────────────────────────────────

function wrapCDATA(text: string): string {
  return `<![CDATA[\n${text.replace(/]]>/g, "]]]]><![CDATA[>")}\n]]>`;
}

function serializeQueryXml(query: { name: string; queryText: string; variables: string; headers: string; requestType?: string; restMethod?: string; restPath?: string; restContentType?: string }): string {
  const escName = query.name.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const restFields = query.requestType === "rest"
    ? `\n  <request-type>rest</request-type>\n  <rest-method>${query.restMethod ?? "GET"}</rest-method>\n  <rest-path>${wrapCDATA(query.restPath ?? "/")}</rest-path>\n  <rest-content-type>${query.restContentType ?? "json"}</rest-content-type>`
    : "";
  return `<spuerhund-query>
  <name>${escName}</name>${restFields}
  <query>${wrapCDATA(query.queryText)}</query>
  <variables>${wrapCDATA(query.variables || "{}")}</variables>
  <headers>${wrapCDATA(query.headers || "{}")}</headers>
</spuerhund-query>`;
}

function serializeQueriesXml(queries: { name: string; queryText: string; variables: string; headers: string }[]): string {
  return queries.map(serializeQueryXml).join("\n\n");
}

interface ParsedQuery {
  name: string;
  queryText: string;
  variables: string;
  headers: string;
  requestType?: string;
  restMethod?: string;
  restPath?: string;
  restContentType?: string;
}

function parseQueryXml(raw: string): ParsedQuery[] {
  const firstAngle = raw.indexOf("<");
  const lastAngle = raw.lastIndexOf(">");
  if (firstAngle === -1 || lastAngle === -1 || lastAngle <= firstAngle) {
    throw new Error("No XML tags found in input");
  }
  const xmlStr = raw.slice(firstAngle, lastAngle + 1);

  // Wrap in a root element to support multiple queries
  const wrapped = `<root>${xmlStr}</root>`;
  const parser = new DOMParser();
  const doc = parser.parseFromString(wrapped, "text/xml");
  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    throw new Error("Invalid XML: " + parseError.textContent?.slice(0, 100));
  }

  const elements = doc.querySelectorAll("spuerhund-query");
  if (elements.length === 0) {
    throw new Error("No <spuerhund-query> elements found");
  }

  const getText = (el: Element | null): string => (el?.textContent ?? "").trim();

  const results: ParsedQuery[] = [];
  for (const el of elements) {
    const nameEl = el.querySelector("name");
    const queryEl = el.querySelector("query");
    if (!nameEl || !queryEl) continue;
    const parsed: ParsedQuery = {
      name: getText(nameEl),
      queryText: getText(queryEl),
      variables: getText(el.querySelector("variables")) || "{}",
      headers: getText(el.querySelector("headers")) || "{}",
    };
    const rt = getText(el.querySelector("request-type"));
    if (rt) parsed.requestType = rt;
    const rm = getText(el.querySelector("rest-method"));
    if (rm) parsed.restMethod = rm;
    const rp = getText(el.querySelector("rest-path"));
    if (rp) parsed.restPath = rp;
    const rct = getText(el.querySelector("rest-content-type"));
    if (rct) parsed.restContentType = rct;
    results.push(parsed);
  }

  if (results.length === 0) {
    throw new Error("No valid queries found in XML");
  }

  return results;
}

function buildLlmPrompt(queryXml: string): string {
  return `Please format your GraphQL query as a copy-pasteable Spürhund query using this XML format:

<spuerhund-query>
  <name>Query Name</name>
  <query><![CDATA[
your query here
]]></query>
  <variables><![CDATA[
{"key": "value"}
]]></variables>
  <headers><![CDATA[
{}
]]></headers>
</spuerhund-query>

Here is my current query for reference:

${queryXml}`;
}

// ── Relative Time ────────────────────────────────────────────────────

function formatRelativeTime(isoString: string | null | undefined): string {
  if (!isoString) return "";
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  if (diffMs < 0) return "";

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  // Check if it was yesterday
  const nowDate = new Date(now);
  const thenDate = new Date(then);
  const dayDiff = Math.floor((nowDate.setHours(0, 0, 0, 0) - thenDate.setHours(0, 0, 0, 0)) / 86400000);
  if (dayDiff === 1) return "yesterday";

  if (dayDiff < 14) return `${dayDiff}d`;

  const weeks = Math.floor(dayDiff / 7);
  if (weeks < 8) return `${weeks}w`;

  const months = Math.floor(dayDiff / 30);
  return `${months}mo`;
}

// ── Sort Helpers ─────────────────────────────────────────────────────

function sortQueriesByName(a: StoredQuery, b: StoredQuery): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

function queryCategoryName(query: StoredQuery): string {
  return query.category?.trim() || "Saved";
}

function buildQueryGroups(queries: StoredQuery[]) {
  const adHoc = queries
    .filter((query) =>
      query.systemPreset === "adhoc-gql"
      || query.systemPreset === "adhoc-rest"
      || query.systemPreset === "adhoc-mongo"
      || query.systemPreset === "adhoc-sql",
    )
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const saved = queries.filter((query) => !isAdHocQuery(query));
  const pinned = saved
    .filter((query) => query.pinnedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const categoryMap = new Map<string, StoredQuery[]>();
  for (const query of saved.filter((entry) => !entry.pinnedAt)) {
    const category = queryCategoryName(query);
    const group = categoryMap.get(category) ?? [];
    group.push(query);
    categoryMap.set(category, group);
  }

  const categories = Array.from(categoryMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: "base" }))
    .map(([name, entries]) => ({
      name,
      queries: entries.sort(sortQueriesByName),
    }));

  return {
    adHoc,
    pinned,
    categories,
  };
}

// ── Header Menu ──────────────────────────────────────────────────────

function HeaderMenu() {
  const t = useGT();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeServerId = useServersStore((s) => s.activeServerId);
  const queries = useQueriesStore((s) => s.queries);
  const activeQueryId = useQueriesStore((s) => s.activeQueryId);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [open]);

  const handleImportFromClipboard = async () => {
    setOpen(false);
    if (!activeServerId) return;
    try {
      const text = await navigator.clipboard.readText();
      const parsed = parseQueryXml(text);
      let lastQuery;
      for (const p of parsed) {
        lastQuery = await addQuery(activeServerId, p.name, {
          queryText: p.queryText,
          variables: p.variables,
          headers: p.headers,
          ...(p.requestType === "rest" ? {
            requestType: "rest" as const,
            restMethod: (p.restMethod ?? "GET") as import("../persistence").RestMethod,
            restPath: p.restPath ?? "/",
            restContentType: (p.restContentType ?? "json") as import("../persistence").RestContentType,
          } : {}),
        });
      }
      if (parsed.length === 1) {
        toast(t("Query imported from clipboard"), "success");
      } else {
        toast(t("{count} queries imported from clipboard", { count: parsed.length }), "success");
      }
    } catch (err) {
      toast(t("Import failed: {message}", { message: err instanceof Error ? err.message : String(err) }), "error");
    }
  };

  const handleCopyAsLlmPrompt = async () => {
    setOpen(false);
    const query = queries.find((q) => q.id === activeQueryId);
    if (!query) {
      toast(t("No active query to copy"), "error");
      return;
    }
    try {
      const xml = serializeQueryXml(query);
      const prompt = buildLlmPrompt(xml);
      await navigator.clipboard.writeText(prompt);
      toast(t("LLM prompt copied to clipboard"), "success");
    } catch (err) {
      toast(t("Copy failed: {message}", { message: err instanceof Error ? err.message : String(err) }), "error");
    }
  };

  const hasActiveQuery = !!activeQueryId;

  return (
    <div className="flex items-center gap-0.5">
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center rounded p-1 text-zinc-500 hover:bg-surface-overlay hover:text-zinc-300"
          title={t("More actions")}
        >
          <MoreVertical size={14} />
        </button>

        {open && (
          <div className="absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded border border-border bg-surface-raised py-1 shadow-xl">
            <button
              onClick={handleImportFromClipboard}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-zinc-300 hover:bg-surface-overlay"
            >
              <ClipboardPaste size={12} />
              {t("Import from Clipboard")}
            </button>
            <button
              onClick={handleCopyAsLlmPrompt}
              disabled={!hasActiveQuery}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ${
                hasActiveQuery ? "text-zinc-300 hover:bg-surface-overlay" : "text-zinc-600 cursor-not-allowed"
              }`}
            >
              <MessageSquareText size={12} />
              {t("Copy as LLM Prompt")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Context Menu ──────────────────────────────────────────────────────

interface ContextMenuState {
  queryId: string;
  queryName: string;
  isPinned: boolean;
  x: number;
  y: number;
}

function ContextMenu({
  state,
  onClose,
  onCopy,
  onRename,
  onOpenPane,
  onTogglePin,
  onDelete,
}: {
  state: ContextMenuState;
  onClose: () => void;
  onCopy: () => void;
  onRename: () => void;
  onOpenPane: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
}) {
  const t = useGT();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [onClose]);

  const items = [
    { label: t("Copy to Clipboard"), icon: Copy, action: onCopy, shortcut: isMac ? "⌘C" : "Ctrl+C" },
    { label: t("Rename"), icon: Pencil, action: onRename, shortcut: "F2" },
    { label: state.isPinned ? t("Unpin") : t("Pin to Top"), icon: state.isPinned ? PinOff : Pin, action: onTogglePin },
    { label: t("Open in New Panel"), icon: PanelRight, action: onOpenPane },
    { label: t("Delete"), icon: Trash2, action: onDelete, danger: true },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[160px] rounded border border-border bg-surface-raised py-1 shadow-xl"
      style={{ left: state.x, top: state.y }}
    >
      {items.map((item) => (
        <button
          key={item.label}
          onClick={() => { item.action(); onClose(); }}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ${
            item.danger
              ? "text-danger hover:bg-danger-bg"
              : "text-zinc-300 hover:bg-surface-overlay"
          }`}
        >
          <item.icon size={12} />
          <span className="flex-1">{item.label}</span>
          {item.shortcut && (
            <span className="text-[10px] text-zinc-600">{item.shortcut}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Batch Action Bar ─────────────────────────────────────────────────

function BatchActionBar({
  selectedIds,
  queries,
  onClearSelection,
}: {
  selectedIds: Set<string>;
  queries: StoredQuery[];
  onClearSelection: () => void;
}) {
  const t = useGT();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const count = selectedIds.size;

  const handleCopy = async () => {
    const selected = queries.filter((q) => selectedIds.has(q.id));
    if (selected.length === 0) return;
    try {
      const xml = serializeQueriesXml(selected);
      await navigator.clipboard.writeText(xml);
      toast(selected.length === 1 ? t("1 query copied") : t("{count} queries copied", { count: selected.length }), "success");
    } catch (err) {
      toast(t("Copy failed: {message}", { message: err instanceof Error ? err.message : String(err) }), "error");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    const ids = Array.from(selectedIds);
    await deleteQueries(ids);
    onClearSelection();
    setConfirmDelete(false);
    toast(ids.length === 1 ? t("1 query deleted") : t("{count} queries deleted", { count: ids.length }), "success");
  };

  const handlePin = async () => {
    await toggleQueriesPin(Array.from(selectedIds));
    onClearSelection();
  };

  const allPinned = Array.from(selectedIds).every((id) => queries.find((q) => q.id === id)?.pinnedAt);

  return (
    <div className="flex items-center gap-1 border-t border-border px-2 py-1.5 bg-surface-raised">
      <span className="text-[10px] text-zinc-500 mr-auto">{t("{count} selected", { count })}</span>
      <button
        onClick={handleCopy}
        className="rounded p-1 text-zinc-400 hover:bg-surface-overlay hover:text-zinc-300"
        title={t("Copy to Clipboard")}
      >
        <Copy size={12} />
      </button>
      <button
        onClick={handlePin}
        className="rounded p-1 text-zinc-400 hover:bg-surface-overlay hover:text-zinc-300"
        title={allPinned ? t("Unpin") : t("Pin")}
      >
        {allPinned ? <PinOff size={12} /> : <Pin size={12} />}
      </button>
      {confirmDelete ? (
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleDelete}
            className="rounded p-1 text-danger hover:bg-danger-bg"
            title={t("Confirm delete")}
          >
            <Check size={12} />
          </button>
          <button
            onClick={() => setConfirmDelete(false)}
            className="rounded p-1 text-zinc-500 hover:bg-surface-overlay"
            title={t("Cancel")}
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <button
          onClick={handleDelete}
          className="rounded p-1 text-zinc-400 hover:bg-danger-bg hover:text-danger"
          title={t("Delete selected")}
        >
          <Trash2 size={12} />
        </button>
      )}
      <button
        onClick={onClearSelection}
        className="rounded p-1 text-zinc-500 hover:bg-surface-overlay hover:text-zinc-300"
        title={t("Clear selection")}
      >
        <X size={12} />
      </button>
    </div>
  );
}

// ── Query Row ────────────────────────────────────────────────────────

function QueryRow({
  query,
  isActive,
  isExecuting,
  isOpenAsPane,
  isRenaming,
  renameValue,
  isSelected,
  confirmDeleteId,
  isPinned,
  canManage,
  draggable: isDraggable,
  selectionActive,
  onSelect,
  onDoubleClick,
  onContextMenu,
  onToggleSelect,
  onStartRename,
  onRenameChange,
  onCommitRename,
  onCancelRename,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  renameInputRef,
}: {
  query: StoredQuery;
  isActive: boolean;
  isExecuting: boolean;
  isOpenAsPane: boolean;
  isRenaming: boolean;
  renameValue: string;
  isSelected: boolean;
  confirmDeleteId: string | null;
  isPinned: boolean;
  canManage: boolean;
  draggable: boolean;
  selectionActive: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onToggleSelect: (e: React.MouseEvent) => void;
  onStartRename: () => void;
  onRenameChange: (v: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onRequestDelete: (e: React.MouseEvent) => void;
  onConfirmDelete: (e: React.MouseEvent) => void;
  onCancelDelete: (e: React.MouseEvent) => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onDrop?: (e: React.DragEvent) => void;
  renameInputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const t = useGT();
  const timeLabel = formatRelativeTime(query.lastUsedAt);

  return (
    <div
      className="relative"
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
    >
      <button
        onClick={onSelect}
        onDoubleClick={onDoubleClick}
        onContextMenu={canManage ? onContextMenu : undefined}
        className={`group flex w-full items-center gap-1 rounded px-1.5 py-1.5 text-left text-xs ${
          isActive
            ? "bg-surface-overlay/60 text-brand"
            : "text-zinc-400 hover:bg-surface-raised hover:text-zinc-300"
        }`}
      >
        {/* Checkbox — only visible when selection is active */}
        {selectionActive && canManage && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {}}
            onClick={onToggleSelect}
            className="h-3 w-3 shrink-0 cursor-pointer accent-brand"
          />
        )}

        {/* Drag handle for pinned */}
        {isDraggable && canManage && (
          <GripVertical
            size={10}
            className="shrink-0 cursor-grab text-zinc-600 opacity-0 group-hover:opacity-100"
          />
        )}

        {isExecuting
          ? <Loader2 size={11} className="shrink-0 animate-spin text-brand" />
          : query.requestType === "rest"
            ? <Globe size={11} className="shrink-0" />
            : <GqlIcon size={11} className="shrink-0" />
        }

        {isRenaming ? (
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onBlur={onCommitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); onCommitRename(); }
              else if (e.key === "Escape") { e.preventDefault(); onCancelRename(); }
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 truncate bg-transparent text-xs text-inherit outline-none"
          />
        ) : (
          <span className="flex-1 truncate">{query.name}</span>
        )}

        {/* Last-used time */}
        {timeLabel && (
          <span className="shrink-0 text-[9px] text-zinc-600">{timeLabel}</span>
        )}

        {isOpenAsPane && (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning" title={t("Open in panel")} />
        )}

        {canManage && (
          <button
            onClick={onRequestDelete}
            className="shrink-0 rounded p-0.5 text-zinc-600 opacity-0 hover:text-danger group-hover:opacity-100"
            title={t("Delete query")}
          >
            <Trash2 size={10} />
          </button>
        )}
      </button>

      {confirmDeleteId === query.id && (
        <div className="absolute right-1 top-1/2 z-20 -translate-y-1/2 flex items-center gap-1 rounded border border-border bg-surface-raised px-2 py-1 shadow-lg">
          <span className="text-[10px] text-zinc-400 mr-1">{t("Delete?")}</span>
          <button onClick={onConfirmDelete} className="rounded p-0.5 text-danger hover:bg-danger-bg" title={t("Confirm delete")}>
            <Check size={12} />
          </button>
          <button onClick={onCancelDelete} className="rounded p-0.5 text-zinc-500 hover:bg-surface-overlay" title={t("Cancel")}>
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Queries Section ───────────────────────────────────────────────────

function QueriesSection() {
  const t = useGT();
  const activeServerId = useServersStore((s) => s.activeServerId);
  const queries = useQueriesStore((s) => s.queries);
  const activeQueryId = useQueriesStore((s) => s.activeQueryId);
  const activePaneId = useAppStore((s) => s.activePaneId);
  const secondaryPanels = useAppStore((s) => s.secondaryPanels);
  const openSecondaryPane = useAppStore((s) => s.openSecondaryPane);

  const [renamingQueryId, setRenamingQueryId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastClickedId, setLastClickedId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragTargetKey, setDragTargetKey] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const executingQueryIds = useExecutionTrackingStore((s) => s.executingQueryIds);
  const groups = useMemo(() => buildQueryGroups(queries), [queries]);
  const secondaryPaneQueryIds = useMemo(
    () => new Set(secondaryPanels.map((pane) => pane.queryId)),
    [secondaryPanels],
  );
  const focusedQueryId = useMemo(
    () =>
      activePaneId === "primary"
        ? activeQueryId
        : secondaryPanels.find((pane) => pane.id === activePaneId)?.queryId ?? activeQueryId,
    [activePaneId, activeQueryId, secondaryPanels],
  );
  const manageableQueries = useMemo(
    () => queries.filter((query) => !isAdHocQuery(query)),
    [queries],
  );
  const allSorted = useMemo(
    () => [
      ...groups.pinned,
      ...groups.categories.flatMap((group) => group.queries),
    ],
    [groups],
  );

  useEffect(() => {
    if (renamingQueryId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingQueryId]);

  // Close confirm popup on Escape
  useEffect(() => {
    if (!confirmDeleteId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConfirmDeleteId(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [confirmDeleteId]);

  // F2 to rename active query
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "F2" && focusedQueryId) {
        e.preventDefault();
        const query = queries.find((q) => q.id === focusedQueryId);
        if (query && !isAdHocQuery(query)) handleStartRename(query.id, query.name);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [focusedQueryId, queries]);

  // Escape clears selection
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedIds.size > 0) {
        setSelectedIds(new Set());
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [selectedIds]);

  // Clean up selected IDs when queries change (deleted queries)
  useEffect(() => {
    const queryIds = new Set(queries.map((q) => q.id));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (queryIds.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [queries]);

  const handleStartRename = (queryId: string, currentName: string) => {
    const query = queries.find((entry) => entry.id === queryId);
    if (!query || isAdHocQuery(query)) return;
    setRenamingQueryId(queryId);
    setRenameValue(currentName);
  };

  const handleCommitRename = async () => {
    if (!renamingQueryId) return;
    const trimmed = renameValue.trim();
    if (trimmed) {
      await updateQueryInDb(renamingQueryId, { name: trimmed });
    }
    setRenamingQueryId(null);
  };

  const handleCancelRename = () => {
    setRenamingQueryId(null);
  };

  const handleSelectQuery = (e: React.MouseEvent, query: StoredQuery) => {
    if (isAdHocQuery(query)) {
      void selectQueryInFocusedPane(query);
      return;
    }
    // Cmd/Ctrl-click toggles selection
    if (e.metaKey || e.ctrlKey) {
      handleToggleSelect(e, query.id);
      return;
    }
    // If selection is active, clicking toggles instead of navigating
    if (selectedIds.size > 0) {
      handleToggleSelect(e, query.id);
      return;
    }
    void selectQueryInFocusedPane(query);
  };

  const handleDoubleClick = useCallback((e: React.MouseEvent, queryId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const query = queries.find((entry) => entry.id === queryId);
    if (!query || isAdHocQuery(query)) return;
    handleStartRename(query.id, query.name);
  }, [queries]);

  const handleContextMenu = useCallback((e: React.MouseEvent, query: StoredQuery) => {
    if (isAdHocQuery(query)) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      queryId: query.id,
      queryName: query.name,
      isPinned: !!query.pinnedAt,
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  const handleCopyQueryToClipboard = useCallback(async (queryId: string) => {
    const query = queries.find((entry) => entry.id === queryId);
    if (!query) return;
    try {
      await navigator.clipboard.writeText(serializeQueryXml(query));
      toast(t("Query copied"), "success");
    } catch (err) {
      toast(t("Copy failed: {message}", { message: err instanceof Error ? err.message : String(err) }), "error");
    }
  }, [queries, t]);

  const selectionActive = selectedIds.size > 0;

  const handleToggleSelect = useCallback((e: React.MouseEvent, queryId: string) => {
    e.stopPropagation();
    const query = queries.find((entry) => entry.id === queryId);
    if (!query || isAdHocQuery(query)) return;

    if (e.shiftKey && lastClickedId) {
      // Range select
      const startIdx = allSorted.findIndex((q) => q.id === lastClickedId);
      const endIdx = allSorted.findIndex((q) => q.id === queryId);
      if (startIdx >= 0 && endIdx >= 0) {
        const from = Math.min(startIdx, endIdx);
        const to = Math.max(startIdx, endIdx);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (let i = from; i <= to; i++) {
            next.add(allSorted[i]!.id);
          }
          return next;
        });
        setLastClickedId(queryId);
        return;
      }
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(queryId)) next.delete(queryId);
      else next.add(queryId);
      return next;
    });
    setLastClickedId(queryId);
  }, [lastClickedId, allSorted, queries]);

  const handleRequestDelete = (e: React.MouseEvent, queryId: string) => {
    e.stopPropagation();
    const query = queries.find((entry) => entry.id === queryId);
    if (!query || isAdHocQuery(query)) return;
    setConfirmDeleteId(queryId);
  };

  const handleConfirmDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirmDeleteId) return;
    await deleteQuery(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  // Drag-and-drop for pinned queries
  const handleDragStart = useCallback((e: React.DragEvent, queryId: string) => {
    const query = queries.find((entry) => entry.id === queryId);
    if (!query || isAdHocQuery(query)) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", queryId);
    setDraggedId(queryId);
  }, [queries]);

  const handleDragOverTarget = useCallback((e: React.DragEvent, targetKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragTargetKey(targetKey);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragTargetKey(null);
  }, []);

  const handleDropOnPinnedRow = useCallback(async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData("text/plain");
    setDraggedId(null);
    setDragTargetKey(null);
    if (!activeServerId || sourceId === targetId) return;

    const sourceQuery = queries.find((query) => query.id === sourceId);
    const pinnedIds = groups.pinned.map((query) => query.id).filter((id) => id !== sourceId);
    if (!sourceQuery || isAdHocQuery(sourceQuery)) return;

    if (sourceQuery.pinnedAt) {
      const targetIndex = pinnedIds.indexOf(targetId);
      if (targetIndex === -1) return;
      pinnedIds.splice(targetIndex, 0, sourceId);
      await reorderPinnedQueries(activeServerId, pinnedIds);
      return;
    }

    await moveSavedQueryToPinned(sourceId, targetId);
  }, [activeServerId, groups.pinned, queries]);

  const handleDropOnPinnedSection = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData("text/plain");
    setDraggedId(null);
    setDragTargetKey(null);
    if (!sourceId) return;
    await moveSavedQueryToPinned(sourceId);
  }, []);

  const handleDropOnCategory = useCallback(async (e: React.DragEvent, category: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData("text/plain");
    setDraggedId(null);
    setDragTargetKey(null);
    if (!sourceId) return;
    await moveSavedQueryToCategory(sourceId, category);
  }, []);

  if (!activeServerId) {
    return (
      <div className="flex flex-1 items-center justify-center p-4 text-center text-xs text-zinc-600">
        {t("Select a server from the header")}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-2">
        {groups.adHoc.length > 0 && (
          <>
            <div className="flex items-center gap-1.5 px-1.5 pb-1 pt-0.5">
              <span className="text-[9px] font-medium uppercase tracking-wider text-zinc-600">{t("AdHoc")}</span>
            </div>
            {groups.adHoc.map((query) => (
              <QueryRow
                key={query.id}
                query={query}
                isActive={focusedQueryId === query.id}
                isExecuting={executingQueryIds.has(query.id)}
                isOpenAsPane={secondaryPaneQueryIds.has(query.id)}
                isRenaming={false}
                renameValue=""
                isSelected={false}
                confirmDeleteId={null}
                isPinned={false}
                canManage={false}
                draggable={false}
                selectionActive={false}
                onSelect={(e) => handleSelectQuery(e, query)}
                onDoubleClick={(e) => handleDoubleClick(e, query.id)}
                onContextMenu={() => {}}
                onToggleSelect={() => {}}
                onStartRename={() => {}}
                onRenameChange={() => {}}
                onCommitRename={() => {}}
                onCancelRename={() => {}}
                onRequestDelete={() => {}}
                onConfirmDelete={() => {}}
                onCancelDelete={() => {}}
                renameInputRef={renameInputRef}
              />
            ))}
            <div className="mx-1.5 my-1 border-t border-border" />
          </>
        )}

        <div
          className={`rounded ${dragTargetKey === "group:pinned" ? "bg-surface-overlay/40" : ""}`}
          onDragOver={(e) => handleDragOverTarget(e, "group:pinned")}
          onDrop={handleDropOnPinnedSection}
        >
          {groups.pinned.length > 0 && (
            <>
              <div className="flex items-center gap-1.5 px-1.5 pb-1 pt-0.5">
                <Pin size={9} className="text-zinc-600" />
                <span className="text-[9px] font-medium uppercase tracking-wider text-zinc-600">{t("Pinned")}</span>
              </div>
              {groups.pinned.map((query) => (
                <div
                  key={query.id}
                  className={dragTargetKey === `row:${query.id}` && draggedId !== query.id ? "border-t border-brand" : ""}
                >
                  <QueryRow
                    query={query}
                    isActive={focusedQueryId === query.id}
                    isExecuting={executingQueryIds.has(query.id)}
                    isOpenAsPane={secondaryPaneQueryIds.has(query.id)}
                    isRenaming={renamingQueryId === query.id}
                    renameValue={renameValue}
                    isSelected={selectedIds.has(query.id)}
                    confirmDeleteId={confirmDeleteId}
                    isPinned
                    canManage
                    draggable
                    selectionActive={selectionActive}
                    onSelect={(e) => handleSelectQuery(e, query)}
                    onDoubleClick={(e) => handleDoubleClick(e, query.id)}
                    onContextMenu={(e) => handleContextMenu(e, query)}
                    onToggleSelect={(e) => handleToggleSelect(e, query.id)}
                    onStartRename={() => handleStartRename(query.id, query.name)}
                    onRenameChange={setRenameValue}
                    onCommitRename={handleCommitRename}
                    onCancelRename={handleCancelRename}
                    onRequestDelete={(e) => handleRequestDelete(e, query.id)}
                    onConfirmDelete={handleConfirmDelete}
                    onCancelDelete={handleCancelDelete}
                    onDragStart={(e) => handleDragStart(e, query.id)}
                    onDragOver={(e) => handleDragOverTarget(e, `row:${query.id}`)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => void handleDropOnPinnedRow(e, query.id)}
                    renameInputRef={renameInputRef}
                  />
                </div>
              ))}
              <div className="mx-1.5 my-1 border-t border-border" />
            </>
          )}
        </div>

        {groups.categories.map((group) => (
          <div
            key={group.name}
            className={`mb-2 rounded ${dragTargetKey === `group:${group.name}` ? "bg-surface-overlay/30" : ""}`}
            onDragOver={(e) => handleDragOverTarget(e, `group:${group.name}`)}
            onDrop={(e) => void handleDropOnCategory(e, group.name)}
          >
            <div className="px-1.5 pb-1 pt-0.5 text-[9px] font-medium uppercase tracking-wider text-zinc-600">
              {group.name}
            </div>
            {group.queries.map((query) => (
              <div
                key={query.id}
                className={dragTargetKey === `row:${query.id}` && draggedId !== query.id ? "border-t border-brand" : ""}
              >
                <QueryRow
                  query={query}
                  isActive={focusedQueryId === query.id}
                  isExecuting={executingQueryIds.has(query.id)}
                  isOpenAsPane={secondaryPaneQueryIds.has(query.id)}
                  isRenaming={renamingQueryId === query.id}
                  renameValue={renameValue}
                  isSelected={selectedIds.has(query.id)}
                  confirmDeleteId={confirmDeleteId}
                  isPinned={false}
                  canManage
                  draggable
                  selectionActive={selectionActive}
                  onSelect={(e) => handleSelectQuery(e, query)}
                  onDoubleClick={(e) => handleDoubleClick(e, query.id)}
                  onContextMenu={(e) => handleContextMenu(e, query)}
                  onToggleSelect={(e) => handleToggleSelect(e, query.id)}
                  onStartRename={() => handleStartRename(query.id, query.name)}
                  onRenameChange={setRenameValue}
                  onCommitRename={handleCommitRename}
                  onCancelRename={handleCancelRename}
                  onRequestDelete={(e) => handleRequestDelete(e, query.id)}
                  onConfirmDelete={handleConfirmDelete}
                  onCancelDelete={handleCancelDelete}
                  onDragStart={(e) => handleDragStart(e, query.id)}
                  onDragOver={(e) => handleDragOverTarget(e, `row:${query.id}`)}
                  onDragEnd={handleDragEnd}
                  renameInputRef={renameInputRef}
                />
              </div>
            ))}
          </div>
        ))}

        {groups.categories.length === 0 && groups.pinned.length === 0 && (
          <div className="px-2 py-3 text-xs text-zinc-600">
            Save an AdHoc request to create pinned or categorized queries.
          </div>
        )}
      </div>

      {selectedIds.size === 0 && manageableQueries.length > 1 && (
        <div className="shrink-0 border-t border-border px-3 py-1.5 text-center text-[10px] text-zinc-600">
          {isMac ? "⌘" : "Ctrl"}+Click to select &middot; drag into Pinned or a category
        </div>
      )}

      {selectedIds.size > 0 && (
        <BatchActionBar
          selectedIds={selectedIds}
          queries={manageableQueries}
          onClearSelection={() => setSelectedIds(new Set())}
        />
      )}

      {contextMenu && (
        <ContextMenu
          state={contextMenu}
          onClose={() => setContextMenu(null)}
          onCopy={() => void handleCopyQueryToClipboard(contextMenu.queryId)}
          onRename={() => handleStartRename(contextMenu.queryId, contextMenu.queryName)}
          onOpenPane={() => openSecondaryPane(contextMenu.queryId)}
          onTogglePin={() => toggleQueryPin(contextMenu.queryId)}
          onDelete={() => setConfirmDeleteId(contextMenu.queryId)}
        />
      )}
    </div>
  );
}

// ── Main Sidebar ──────────────────────────────────────────────────────

export function Sidebar({ style }: { style?: React.CSSProperties }) {
  const t = useGT();
  const setShowSchemaBrowser = useAppStore((s) => s.setShowSchemaBrowser);
  return (
    <aside className="flex shrink-0 flex-col border-r border-border bg-surface-raised overflow-hidden" style={style}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          {t("Requests")}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSchemaBrowser(true)}
            className="flex items-center rounded p-1 text-zinc-500 hover:bg-surface-overlay hover:text-zinc-300"
            title={t("Browse server schema")}
          >
            <BookOpen size={14} />
          </button>
          <HeaderMenu />
        </div>
      </div>
      <QueriesSection />
    </aside>
  );
}
